import {
  NetworkCoverageMapDataContext,
  NetworkCoverageMapDataContextProps
} from "@app/projects/network-assessment/maps/network-coverage-map/contexts/NetworkCoverageMapDataContext";
import { ProvidersFilterOptions } from "@app/projects/network-assessment/maps/network-coverage-map/enumerators";
import {
  HUNDRED_PERCENT_FILTER,
  ZERO_PERCENT_FILTER
} from "@app/projects/network-assessment/maps/network-coverage-map/NetworkCoverageMapsConstants";
import RoundCheckbox from "@app/shared/RoundCheckbox";
import DualRangeSlider from "@app/shared/DualRangeSlider";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { MapCustomControlProps } from "@app/shared/mapping/MapCustomControlProps";
import { FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Toggle } from "@app/shared/Toggle";
import { ProviderActionStatusEnum } from "@app/projects/enumerators";
import {
  ConvertProviderSourceFilterOptionsToDisplay
} from "@app/projects/network-assessment/maps/network-coverage-map/DisplayConverters";
import { QesActiveFiltersBar } from "@app/shared/QesActiveFiltersBar";
import type { FilterChipDescriptor } from "@app/shared/QesActiveFiltersBar";
import { ClaimsActivityLineOfBusinessTypeEnum, ClaimsActivityVolumeLabelEnum } from "@app/projects/network-assessment/pci-dashboard/claims-activity";
import { FilterContext, FilterContextProps } from '@app/projects/network-assessment/maps/network-coverage-map/contexts/FilterContext';
import { Accordion } from "@app/shared/Accordion";
import { AccordionContainer } from "@app/shared/AccordionContainer";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import MultiselectDropdown from "@app/shared/checkbox-dropdown/MultiselectDropdown";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import ClaimsActivityLineOfBusinessFilter from "@app/projects/network-assessment/pci-dashboard/claims-activity/ClaimsActivityLineOfBusinessFilter";


const NetworkCoverageMapFloatingFilterControl: FC<MapCustomControlProps> = ({ ignoreInMapExport }) => {

  const projectContext = useContext(CountyProjectContext);
  const mapDataContext = useContext(NetworkCoverageMapDataContext) as NetworkCoverageMapDataContextProps;
  const filterContext = useContext(FilterContext) as FilterContextProps;

  const adequacyTimeslices = filterContext.adequacyTimeslices;
  const pciDataEnabled = adequacyTimeslices.find(prc => prc.projectRunConfigurationId == filterContext.selectedTimeslice)?.showPciData ?? false;

  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);

  const [filterGainsMinPercent, setFilterGainsMinPercent] = useState<number>(mapDataContext.filterGainsMinPercent);
  const [filterGainsMaxPercent, setFilterGainsMaxPercent] = useState<number>(mapDataContext.filterGainsMaxPercent);
  const [filterServesMinPercent, setFilterServesMinPercent] = useState<number>(mapDataContext.filterServesMinPercent);
  const [filterServesMaxPercent, setFilterServesMaxPercent] = useState<number>(mapDataContext.filterServesMaxPercent);
  const [filterImpactsMinPercent, setFilterImpactsMinPercent] = useState<number>(mapDataContext.filterImpactsMinPercent);
  const [filterImpactsMaxPercent, setFilterImpactsMaxPercent] = useState<number>(mapDataContext.filterImpactsMaxPercent);
  const [selectedProviderFilterOptions, setSelectedProviderFilterOptions] = useState<ProvidersFilterOptions[]>(mapDataContext.selectedProviderFilterOptions);
  const [selectedLineOfBusiness, setSelectedLineOfBusiness] = useState<ClaimsActivityLineOfBusinessTypeEnum | null>(mapDataContext.selectedLineOfBusiness);
  const [selectedVolumeIndicatorsValues, setSelectedVolumeIndicatorValues] = useState<DropdownItem<ClaimsActivityVolumeLabelEnum>[]>(mapDataContext.selectedVolumeIndicators);
  const [selectedEffectiveness, setSelectedEffectiveness] = useState<number[]>(mapDataContext.selectedEffectiveness);
  const [selectedEfficiency, setSelectedEfficiency] = useState<number[]>(mapDataContext.selectedEfficiency);

  const [showExcludedProviders, setShowExcludedProviders] = useState<boolean>(mapDataContext.showExcludedProviders);

  const filterMenuRef = useRef<HTMLDivElement>(null);
  const filtersButtonRef = useRef<HTMLButtonElement>(null);

  // Targeted provider checkboxes aren't checked correctly without this in the event
  // that a user opens the filter menu extremely quickly after the providers are loaded.
  useEffect(() => {
    setSelectedProviderFilterOptions(mapDataContext.selectedProviderFilterOptions);
  }, [mapDataContext.selectedProviderFilterOptions]);

  useEffect(() => {
    if (!selectedLineOfBusiness) {
      setSelectedVolumeIndicatorValues([]);
    }
  }, [selectedLineOfBusiness])

  const appliedProviderSourceChips = useMemo<FilterChipDescriptor[]>(() => {
    return mapDataContext.filterAvailableProviderSources(selectedProviderFilterOptions)
      .sort(mapDataContext.providerSourceFiltersComparer)
      .map((option): FilterChipDescriptor => ({
        content: ConvertProviderSourceFilterOptionsToDisplay[option],
        key: String(option),
        onDismiss: () => setSelectedProviderFilterOptions(selectedProviderFilterOptions.filter((o) => o !== option)),
      }));
  }, [selectedProviderFilterOptions, filterGainsMinPercent, filterGainsMaxPercent, filterServesMinPercent, filterServesMaxPercent, filterImpactsMinPercent, filterImpactsMaxPercent]);

  const appliedProviderMetricsChips = useMemo<FilterChipDescriptor[]>(() => {
    const result: FilterChipDescriptor[] = [];
    if (filterServesMinPercent !== ZERO_PERCENT_FILTER || filterServesMaxPercent !== HUNDRED_PERCENT_FILTER) {
      result.push({
        content: `Serves %: ${filterServesMinPercent}% - ${filterServesMaxPercent}%`,
        key: "serves",
        onDismiss: () => { setFilterServesMinPercent(ZERO_PERCENT_FILTER); setFilterServesMaxPercent(HUNDRED_PERCENT_FILTER); },
      });
    }
    if (filterGainsMinPercent !== ZERO_PERCENT_FILTER || filterGainsMaxPercent !== HUNDRED_PERCENT_FILTER) {
      result.push({
        content: `Gains %: ${filterGainsMinPercent}% - ${filterGainsMaxPercent}%`,
        key: "gains",
        onDismiss: () => { setFilterGainsMinPercent(ZERO_PERCENT_FILTER); setFilterGainsMaxPercent(HUNDRED_PERCENT_FILTER); },
      });
    }
    if (filterImpactsMinPercent !== ZERO_PERCENT_FILTER || filterImpactsMaxPercent !== HUNDRED_PERCENT_FILTER) {
      result.push({
        content: `Impacts %: ${filterImpactsMinPercent}% - ${filterImpactsMaxPercent}%`,
        key: "impacts",
        onDismiss: () => { setFilterImpactsMinPercent(ZERO_PERCENT_FILTER); setFilterImpactsMaxPercent(HUNDRED_PERCENT_FILTER); },
      });
    }
    return result;
  }, [filterGainsMinPercent, filterGainsMaxPercent, filterServesMinPercent, filterServesMaxPercent, filterImpactsMinPercent, filterImpactsMaxPercent]);

  const appliedPciChips = useMemo<FilterChipDescriptor[]>(() => {
    const result: FilterChipDescriptor[] = [];
    if (pciDataEnabled) {
      if (selectedVolumeIndicatorsValues.length > 0 && selectedVolumeIndicatorsValues.length !== filterContext.mappedVolumeIndicators.length) {
        for (const value of selectedVolumeIndicatorsValues) {
          result.push({
            content: `PCI ${selectedLineOfBusiness}: ${value.name}`,
            key: `volumeIndicator-${value.name}`,
            onDismiss: () => setSelectedVolumeIndicatorValues(selectedVolumeIndicatorsValues.filter((v) => v !== value)),
          });
        }
      }
      for (const value of [...selectedEffectiveness].sort((a, b) => a - b)) {
        result.push({
          content: `Effectiveness: ${value ? value : "Unknown"}`,
          key: `effectiveness-${value}`,
          onDismiss: () => setSelectedEffectiveness(selectedEffectiveness.filter((v) => v !== value)),
        });
      }
      for (const value of [...selectedEfficiency].sort((a, b) => a - b)) {
        result.push({
          content: `Efficiency: ${value ? value : "Unknown"}`,
          key: `efficiency-${value}`,
          onDismiss: () => setSelectedEfficiency(selectedEfficiency.filter((v) => v !== value)),
        });
      }
    }
    return result;
  }, [selectedVolumeIndicatorsValues, selectedLineOfBusiness, selectedEffectiveness, selectedEfficiency, pciDataEnabled]);

  const chips = useMemo<FilterChipDescriptor[]>(
    () => [...appliedProviderSourceChips, ...appliedProviderMetricsChips, ...appliedPciChips],
    [appliedProviderSourceChips, appliedProviderMetricsChips, appliedPciChips],
  );

  const cancelFilters = () => {
    setFilterGainsMinPercent(mapDataContext.filterGainsMinPercent);
    setFilterGainsMaxPercent(mapDataContext.filterGainsMaxPercent);
    setFilterServesMinPercent(mapDataContext.filterServesMinPercent);
    setFilterServesMaxPercent(mapDataContext.filterServesMaxPercent);
    setFilterImpactsMinPercent(mapDataContext.filterImpactsMinPercent);
    setFilterImpactsMaxPercent(mapDataContext.filterImpactsMaxPercent);
    setSelectedProviderFilterOptions(mapDataContext.selectedProviderFilterOptions);
    setShowExcludedProviders(mapDataContext.showExcludedProviders);

    if (pciDataEnabled) {
      setSelectedLineOfBusiness(mapDataContext.selectedLineOfBusiness);
      setSelectedEffectiveness(mapDataContext.selectedEffectiveness);
      setSelectedEfficiency(mapDataContext.selectedEfficiency);
      setSelectedVolumeIndicatorValues(mapDataContext.selectedVolumeIndicators);
    }
  }

  const applyFilters = () => {
    mapDataContext.setFilterGainsMinPercent(filterGainsMinPercent);
    mapDataContext.setFilterGainsMaxPercent(filterGainsMaxPercent);
    mapDataContext.setFilterServesMinPercent(filterServesMinPercent);
    mapDataContext.setFilterServesMaxPercent(filterServesMaxPercent);
    mapDataContext.setFilterImpactsMinPercent(filterImpactsMinPercent);
    mapDataContext.setFilterImpactsMaxPercent(filterImpactsMaxPercent);

    mapDataContext.setSelectedProviderFilterOptions(selectedProviderFilterOptions);

    mapDataContext.setShowExcludedProviders(showExcludedProviders);

    if (pciDataEnabled) {
      mapDataContext.setSelectedLineOfBusiness(selectedLineOfBusiness);
      mapDataContext.setSelectedVolumeIndicators(selectedVolumeIndicatorsValues)
      mapDataContext.setSelectedEffectiveness(selectedEffectiveness);
      mapDataContext.setSelectedEfficiency(selectedEfficiency);
    }

    setIsFilterMenuOpen(false);
  }

  const resetFilters = () => {
    setSelectedProviderFilterOptions([]);
    setFilterServesMinPercent(ZERO_PERCENT_FILTER);
    setFilterServesMaxPercent(HUNDRED_PERCENT_FILTER);
    setFilterGainsMinPercent(ZERO_PERCENT_FILTER);
    setFilterGainsMaxPercent(HUNDRED_PERCENT_FILTER);
    setFilterImpactsMinPercent(ZERO_PERCENT_FILTER);
    setFilterImpactsMaxPercent(HUNDRED_PERCENT_FILTER);

    if (pciDataEnabled) {
      setSelectedLineOfBusiness(null);
      setSelectedEffectiveness([]);
      setSelectedEfficiency([]);
      setSelectedVolumeIndicatorValues([]);
    }
  }

  useEffect(() => {
    const onClickOutside = (event: MouseEvent) => {
      if (!!filterMenuRef?.current &&
          !filterMenuRef.current.contains(event.target as Node) &&
          !filtersButtonRef?.current?.contains(event.target as Node) &&
          isFilterMenuOpen
      ) {
        setIsFilterMenuOpen(false);
        cancelFilters();
      }
    }

    document.addEventListener("pointerdown", onClickOutside);
    return () => {
      document.removeEventListener("pointerdown", onClickOutside);
    }
  }, [isFilterMenuOpen]);

  const hasExcludedProviders = useMemo(() => {
    return mapDataContext.providers?.some(provider => provider.scenarioModelProviderStatus === ProviderActionStatusEnum.FullyExcluded);
  }, [mapDataContext.providers]);

  const getEffectivenessChecked = (value: number): boolean => {
    return selectedEffectiveness.includes(value);
  }

  const getEfficiencyChecked = (value: number): boolean => {
    return selectedEfficiency.includes(value);
  }

  return (
    <div className="floating-filters-control-main-container" {...(ignoreInMapExport ? { 'data-html2canvas-ignore': '' } : {})}>

      <QESButton
        qesTestId="network-coverage-map__filters-button"
        className="floating-filters-button"
        qesType="none"
        disabled={mapDataContext.loadingProviders}
        onClick={() => {
          setIsFilterMenuOpen(!isFilterMenuOpen);
          cancelFilters();
        }}
        style={{
          alignItems: 'center',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
        }}
        qesRef={filtersButtonRef}
      >
        <>
          <QesIcon
            iconName={QesIcons.FilterLines}
            width="24px"
            height="24px"
            isDecorative
          />
          <span className="ms-1 me-1">Filters</span>
          <span className="floating-filters-circle-counter">{chips.length}</span>
          <TooltipWrapper
            text="The map will update to reflect your selected filters. For example, if you choose 'Network Providers' with 'Serves % ≥ 50', only providers meeting that criteria will be shown"
            position="right"
            tooltipWidth="20rem"
          >
            <QesIcon
              iconName={QesIcons.InfoCircle}
              className="floating-filters-button-tooltip-icon"
              height="24px"
              width="24px"
              description="focus or hover for a tooltip"
            />
          </TooltipWrapper>
        </>
      </QESButton>

      {isFilterMenuOpen &&
        <div className="floating-filters-menu-container" ref={filterMenuRef}>
          <div className="floating-filters-menu-header">
            <h4 className="header-s mb-0">Filters</h4>
            <div className="floating-filter-menu-header-right-container">
              <QESButton
                qesTestId="network-coverage-map__floating-filters-menu-reset-filters-button"
                className="qes-button-secondary floating-filters-menu-reset-filters-button"
                qesType="none"
                onClick={() => {
                  resetFilters();
                }}
              >
                Reset Filters
              </QESButton>
              <QESButton
                qesTestId="network-coverage-map__floating-filters-menu-close-button"
                className="qes-secondary floating-filters-menu-close-button"
                qesType="none"
                onClick={() => {
                  setIsFilterMenuOpen(false);
                  cancelFilters();
                }}
              >
                <QesIcon iconName={QesIcons.XClose} width="24px" height="24px" color="#32323C" description="cancel and close menu" />
              </QESButton>
            </div>
          </div>
          <div className="floating-filters-menu-separator" />
          <QesActiveFiltersBar className="floating-filters-applied-filters-container" chips={chips} testIdPrefix="network-coverage-map__floating-filters-applied-filter" variant="minimal" />
          <AccordionContainer className="floating-filters-menu-accordion-container">
            <Accordion title={<>Provider Source and Action Status<span className="floating-filters-circle-counter ms-2">{appliedProviderSourceChips.length}</span></>} defaultOpen={true} testId="network-coverage-map__floating-filters-provider-source-accordion">
              <>
                { hasExcludedProviders && !!projectContext.runConfiguration?.isLatestPublished &&
                <div className="float-end">
                  <Toggle
                    active={showExcludedProviders}
                    testId={"network-coverage-map__floating-filters-menu-show-excluded-providers"}
                    label={"Show Excluded Providers"}
                    labelSide={"right"}
                    size={"small"}
                    onChange={() => {
                      const showExcludedProvidersLocal = !showExcludedProviders; // Handle delay of state change
                      setShowExcludedProviders(showExcludedProvidersLocal);

                      if (!showExcludedProvidersLocal) {
                        const updatedSelectedProviderFilterOptions =
                          selectedProviderFilterOptions.filter(option =>
                            option !== ProvidersFilterOptions.Excluded &&
                            option !== ProvidersFilterOptions.ExcludedMarket &&
                            option !== ProvidersFilterOptions.ExcludedProspect
                          );

                        setSelectedProviderFilterOptions(updatedSelectedProviderFilterOptions);
                      }
                    }}
                  />
                </div>
                }
                {
                    mapDataContext.providersFilterOptions.map((option) => {
                      const isHidden =
                        (option.type === ProvidersFilterOptions.Network && mapDataContext.hideNetworkProviderFilter) ||
                        (option.type === ProvidersFilterOptions.Market && mapDataContext.hideMarketProviderFilter) ||
                        (option.type === ProvidersFilterOptions.Prospect && mapDataContext.hideProspectProviderFilter) ||
                        (!option?.children || option.children.length === 0);

                    return !isHidden ? (
                      <div key={option.type}>
                        <QESCheckbox
                          label={option.name}
                          onChange={() => {
                            if (!!option.children) {
                              // if all the children are selected, unselect the children
                                if (selectedProviderFilterOptions.filter((o) => option.children?.some((child) => child.type === o)).length == option.children?.filter(child => {
                                  if (showExcludedProviders) {
                                    return true;
                                  } else {
                                    return ![ProvidersFilterOptions.ExcludedMarket, ProvidersFilterOptions.ExcludedProspect].includes(child.type);
                                  }
                                }).length) {
                                const selectedChildren = option.children.map((child) => child.type);
                                  setSelectedProviderFilterOptions(selectedProviderFilterOptions.filter((o) => !selectedChildren.includes(o)));
                              }
                              // if not all the children are selected, select the children
                              else {
                                // filter out the children that are already selected (if any) to avoid duplicates
                                const selectedChildren = option.children
                                  .map((child) => child.type)
                                    .filter((childType) => !selectedProviderFilterOptions.includes(childType))
                                    .filter(o => {
                                      if (showExcludedProviders) {
                                        return true;
                                      } else {
                                        return ![ProvidersFilterOptions.ExcludedMarket, ProvidersFilterOptions.ExcludedProspect].includes(o);
                                      }
                                    });
                                  setSelectedProviderFilterOptions([...(selectedProviderFilterOptions || []), ...selectedChildren]);
                              }
                              return;
                            }

                            if (selectedProviderFilterOptions.includes(option.type)) {
                                setSelectedProviderFilterOptions(selectedProviderFilterOptions.filter((o) => o !== option.type));
                            }
                            else {
                                setSelectedProviderFilterOptions([...(selectedProviderFilterOptions || []), option.type]);
                            }
                          }}
                            checked={selectedProviderFilterOptions.includes(option.type) || !!option.children && option.children.length > 0
                              && selectedProviderFilterOptions
                                .filter((o) => option.children?.some((child) => child.type === o))
                                .length == option.children?.filter(child => {
                                if (showExcludedProviders) {
                                  return true;
                                } else {
                                  return ![ProvidersFilterOptions.ExcludedMarket, ProvidersFilterOptions.ExcludedProspect].includes(child.type);
                                }
                              }).length}
                          indeterminate={!!option.children && option.children.length > 0
                              && selectedProviderFilterOptions.filter((o) => option.children?.some((child) => child.type === o)).length > 0
                              && selectedProviderFilterOptions.filter((o) => option.children?.some((child) => child.type === o)).length < option.children?.filter(child => {
                              if (showExcludedProviders) {
                                return true;
                              } else {
                                return ![ProvidersFilterOptions.ExcludedMarket, ProvidersFilterOptions.ExcludedProspect].includes(child.type);
                              }
                            }).length
                          }
                          testId={option.id}
                          containerClassName="action-menu-item col-12"
                        />

                        {!!option?.children && option?.children?.length > 0 &&
                          <div className="ms-3">
                            {option.children
                              .filter(child =>
                              (child.type !== ProvidersFilterOptions.ExcludedMarket && child.type !== ProvidersFilterOptions.ExcludedProspect) ||
                                showExcludedProviders
                            ).map((child) => (
                              <QESCheckbox
                                key={child.type}
                                label={child.name}
                                onChange={() => {
                                    if (selectedProviderFilterOptions.includes(child.type)) {
                                      setSelectedProviderFilterOptions(selectedProviderFilterOptions.filter((o) => o !== child.type));
                                  }
                                  else {
                                      setSelectedProviderFilterOptions([...(selectedProviderFilterOptions || []), child.type]);
                                  }
                                }}
                                  checked={selectedProviderFilterOptions.includes(child.type)}
                                testId={child.id}
                                containerClassName="action-menu-item col-12"
                              />
                            ))}
                          </div>}
                      </div>
                    ) : null;
                  })
                }
              </>
            </Accordion>
            <Accordion
              title={
                <>
                  Provider Contribution Measures
                  <span className="floating-filters-circle-counter ms-2">
                    {appliedProviderMetricsChips.length}
                  </span>
                </>
              }
              defaultOpen={false}
              testId="network-coverage-map__floating-filters-provider-metrics-accordion"
            >
              <>
                <DualRangeSlider
                  testId={"network-coverage-map__filter--serves-percent-slider"}
                  label={
                    <>
                      Serves %
                      <TooltipWrapper
                        text="Applies to both In and Out-of-Network provider sources"
                        position="right"
                        containerClassName="ms-1"
                      >
                        <QesIcon
                          iconName={QesIcons.InfoCircle}
                          description="focus or hover for a tooltip"
                        />
                      </TooltipWrapper>
                    </>
                  }
                  minValue={ZERO_PERCENT_FILTER}
                  maxValue={HUNDRED_PERCENT_FILTER}
                  onChange={([min, max]) => {
                    setFilterServesMinPercent(min);
                    setFilterServesMaxPercent(max);
                  }}
                  value={[filterServesMinPercent, filterServesMaxPercent]}
                />
                <div className="floating-filters-menu-separator mb-2 mt-2"/>
                <DualRangeSlider
                  testId={"network-coverage-map__filter--gains-percent-slider"}
                  label={
                    <>
                      Gains %
                      <TooltipWrapper
                        text="Only applies to Out-of-Network provider sources"
                        position="right"
                        containerClassName="ms-1"
                      >
                        <QesIcon
                          iconName={QesIcons.InfoCircle}
                          description="focus or hover for a tooltip"
                        />
                      </TooltipWrapper>
                    </>
                  }
                  minValue={ZERO_PERCENT_FILTER}
                  maxValue={HUNDRED_PERCENT_FILTER}
                  onChange={([min, max]) => {
                    setFilterGainsMinPercent(min);
                    setFilterGainsMaxPercent(max);
                  }}
                  value={[filterGainsMinPercent, filterGainsMaxPercent]}
                />
                <div className="floating-filters-menu-separator mb-2 mt-2"/>
                <DualRangeSlider
                  testId={"network-coverage-map__filter--impact-percent-slider"}
                  label={
                    <>
                      Impact %
                      <TooltipWrapper
                        text="Only applies to Out-of-Network provider sources"
                        position="right"
                        containerClassName="ms-1"
                      >
                        <QesIcon
                          iconName={QesIcons.InfoCircle}
                          description="focus or hover for a tooltip"
                        />
                      </TooltipWrapper>
                    </>
                  }
                  minValue={ZERO_PERCENT_FILTER}
                  maxValue={HUNDRED_PERCENT_FILTER}
                  onChange={([min, max]) => {
                    setFilterImpactsMinPercent(min);
                    setFilterImpactsMaxPercent(max);
                  }}
                  value={[filterImpactsMinPercent, filterImpactsMaxPercent]}
                />
              </>
            </Accordion>
            {pciDataEnabled ?
              (
                <Accordion title={
                  <>
                    Provider Claims Insights
                    <span className="floating-filters-circle-counter ms-2">
                      {appliedPciChips.length}
                    </span>
                  </>
                  }
                  testId="pci-map-filters">
                  <>
                    <div className="w-50">
                      <ClaimsActivityLineOfBusinessFilter
                        allowEmpty={true}
                        disabled={false}
                        selectedLineOfBusiness={selectedLineOfBusiness}
                        setSelectedLineOfBusiness={setSelectedLineOfBusiness}
                        testId="network-coverage-map__filter--line-of-business"
                        project={projectContext.project}
                      />
                    </div>
                    <div className="mb-2 mt-2" />
                    <MultiselectDropdown
                      disabled={!selectedLineOfBusiness}
                      testId="network-coverage-map__filter--volume-indicator"
                      label="Volume Indicator"
                      containerClassName="w-50 floating-filters-volume-indicator"
                      customSelectInputText={
                        !selectedVolumeIndicatorsValues.length
                          ? "All"
                          : undefined
                      }
                      items={filterContext.mappedVolumeIndicators}
                      selectedItems={selectedVolumeIndicatorsValues}
                      onSelectionChanged={(items) => {
                        setSelectedVolumeIndicatorValues(items);
                      }}
                    />
                    <div className="floating-filters-menu-separator mb-2 mt-2" />
                    <RoundCheckbox
                      includesUnknown={true}
                      label={
                        <span>Quality (Effectiveness) Score</span>
                      }
                      values={filterContext.effectivenessEfficiencyValues}
                      testId="effectiveness"
                      isChecked={getEffectivenessChecked}
                      onChange={(value: number) => {
                        setSelectedEffectiveness(prev =>
                          prev.includes(value)
                            ? prev.filter(v => v !== value)
                            : [...prev, value]
                        );
                      }}
                    />
                    <div className="mb-2 mt-2" />
                    <RoundCheckbox
                      includesUnknown={true}
                      label={
                        <span>Cost (Efficiency) Score</span>
                      }
                      values={filterContext.effectivenessEfficiencyValues}
                      testId="efficiency"
                      isChecked={getEfficiencyChecked}
                      onChange={(value: number) => {
                        setSelectedEfficiency(prev =>
                          prev.includes(value)
                            ? prev.filter(v => v !== value)
                            : [...prev, value]
                        );
                      }}
                    />
                  </>
                </Accordion>
              ) : null
            }
          </AccordionContainer>
          <div className="floating-filters-apply-container floating-filters-menu-content">
            <QESButton
              qesTestId="network-coverage-map__floating-filters-menu-apply-button"
              className=""
              qesType="primary"
              onClick={() => applyFilters()}
            >
              Apply Filters
            </QESButton>
          </div>
        </div>
      }
    </div>
  );
}

export default NetworkCoverageMapFloatingFilterControl;